export interface MembershipState {
  isMember: boolean;
  title: string;
  expiresAt?: number;
  moonsBalance?: number;
  dailyFreeLimit?: number;
  usedFreeToday?: number;
  unreadMessages?: number;
}

export const DEFAULT_MEMBERSHIP: MembershipState = {
  isMember: false,
  title: "免费会员"
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readFirst(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== "") return record[key];
  }
  return undefined;
}

function normalizeTimestamp(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 && value < 100_000_000_000 ? value * 1000 : value;
  }

  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return normalizeTimestamp(numeric);
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return undefined;
}

function normalizeCount(value: unknown) {
  const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : undefined;
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric as number)) : undefined;
}

function normalizeMemberFlag(value: unknown) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes"].includes(normalized)) return true;
    if (["0", "false", "no"].includes(normalized)) return false;
  }
  return undefined;
}

export function normalizeMembership(value?: Partial<MembershipState>): MembershipState {
  const isMember = value?.isMember === true;
  const title = isMember && typeof value?.title === "string" && value.title.trim()
    ? value.title.trim()
    : isMember
      ? "已开通会员"
      : DEFAULT_MEMBERSHIP.title;

  return {
    isMember,
    title,
    expiresAt: normalizeTimestamp(value?.expiresAt),
    moonsBalance: normalizeCount(value?.moonsBalance),
    dailyFreeLimit: normalizeCount(value?.dailyFreeLimit),
    usedFreeToday: normalizeCount(value?.usedFreeToday),
    unreadMessages: normalizeCount(value?.unreadMessages)
  };
}

function readMembershipCandidate(value: unknown): MembershipState | undefined {
  if (!isRecord(value)) return undefined;

  // 会员身份只能来自后端明确返回的会员布尔字段，账号 level、名称或旧本地 tier 都不得推断会员。
  const memberFlag = normalizeMemberFlag(readFirst(value, ["isMember", "member", "is_member"]));
  const titleValue = readFirst(value, ["member_badge", "memberBadge"]);
  const expiresAt = normalizeTimestamp(readFirst(value, ["member_expire_at", "memberExpireAt"]));
  const moonsBalance = normalizeCount(readFirst(value, ["moonsBalance", "moons_balance", "moon_balance", "moonBalance"]));
  const dailyFreeLimit = normalizeCount(readFirst(value, ["dailyFreeLimit", "daily_free_limit"]));
  const usedFreeToday = normalizeCount(readFirst(value, ["usedFreeToday", "used_free_today"]));
  const unreadMessages = normalizeCount(readFirst(value, ["unreadMessages", "unread_messages"]));
  const hasAccountSignal = memberFlag !== undefined || moonsBalance !== undefined || dailyFreeLimit !== undefined || usedFreeToday !== undefined || unreadMessages !== undefined;

  if (!hasAccountSignal) return undefined;

  return normalizeMembership({
    isMember: memberFlag === true,
    title: memberFlag === true && typeof titleValue === "string" ? titleValue : undefined,
    expiresAt: memberFlag === true ? expiresAt : undefined,
    moonsBalance,
    dailyFreeLimit,
    usedFreeToday,
    unreadMessages
  });
}

export function readMembership(value: unknown): MembershipState | undefined {
  if (!isRecord(value)) return undefined;

  const nested = readFirst(value, ["membership", "membership_info", "membershipInfo", "member_info", "memberInfo"]);
  const fromNested = readMembershipCandidate(nested);
  if (fromNested) return fromNested;

  return readMembershipCandidate(value);
}

export function resolveMembership(user: unknown, snapshot?: Partial<MembershipState>): MembershipState {
  return readMembership(user) ?? normalizeMembership(snapshot);
}
